import { action, observable } from 'mobx';
import { SelectOption } from 'types/model/selectOption';
import { IField } from 'components/shared/form/IField';
import { AvailableCoffeeType } from 'opportunities/components/AvailableCoffee';

interface SelectedCustomer extends SelectOption {
  price_unit_currency?: string;
  currency?: string;
  unit_of_measure?: string;
}

interface OpportunityStoreProps {
  fields: IField[];
  arrivedCoffee: AvailableCoffeeType[];
  afloatCoffee: AvailableCoffeeType[];
  selectedIcos: AvailableCoffeeType[];
}

class OpportunityStore {
  @observable public selectedCustomer?: SelectedCustomer;
  @observable public filteredCoffee: AvailableCoffeeType[] = [];
  @observable public originWarehouseId?: string;
  @observable public sellerId?: string;
  @observable public selectedIcos: AvailableCoffeeType[] = [];
  @observable public afloat?: string;
  arrivedCoffee: AvailableCoffeeType[] = [];
  afloatCoffee: AvailableCoffeeType[] = [];
  availableCoffee: AvailableCoffeeType[] = [];

  constructor({
    fields,
    arrivedCoffee,
    afloatCoffee,
    selectedIcos,
  }: OpportunityStoreProps) {
    const customerField = fields.find((field) => field.name === 'customer_id');
    if (customerField?.value) {
      this.selectedCustomer = customerField.options?.find(
        (option) => option.id === customerField.value
      );
    } else {
      // Set default customer when no customer is selected (e.g., when creating a new lead)
      this.setSelectedCustomer(undefined);
    }

    this.originWarehouseId = fields.find(
      (field) => field.name === 'origin_warehouse_id'
    )?.value;

    this.afloat = fields.find((field) => field.name === 'afloat')?.value;

    this.sellerId = fields.find((field) => field.name === 'seller_id')?.value;
    this.arrivedCoffee = arrivedCoffee;
    this.afloatCoffee = afloatCoffee;

    if (this.afloat) {
      this.availableCoffee = afloatCoffee;
      this.setAvailableCoffee(this.availableCoffee, selectedIcos);
      this.setFilteredCoffee('seller_id', this.sellerId);
    } else {
      this.availableCoffee = arrivedCoffee;
      this.setAvailableCoffee(this.availableCoffee, selectedIcos);
      this.setFilteredCoffee('origin_warehouse_id', this.originWarehouseId);
    }

    this.selectedIcos = selectedIcos;
  }

  @action public setSelectedCustomer(customer?: SelectedCustomer) {
    const defaultCustomer: SelectedCustomer = {
      id: 'default',
      name: 'Default Customer',
      currency: 'USD',
      unit_of_measure: 'unit_lb',
      price_unit_currency: 'USD/lb',
    };
    this.selectedCustomer = customer ?? defaultCustomer;
  }

  @action public setAfloat(afloat: string) {
    this.afloat = afloat;
    if (afloat) {
      this.availableCoffee = this.afloatCoffee;
    } else {
      this.availableCoffee = this.arrivedCoffee;
    }
    this.availableCoffee.forEach((c) => (c.checked = false));
    this.filteredCoffee = this.availableCoffee;
    this.originWarehouseId = undefined;
    this.sellerId = undefined;
  }

  @action public setSellerId(sellerId: string) {
    this.sellerId = sellerId;
    this.availableCoffee.forEach((c) => (c.checked = false));
    this.setFilteredCoffee('seller_id', sellerId);
  }

  @action public setOriginWarehouseId(warehouseId: string) {
    this.originWarehouseId = warehouseId;
    this.availableCoffee.forEach((c) => (c.checked = false));
    this.setFilteredCoffee('origin_warehouse_id', warehouseId);
  }
  @action setFilteredCoffee(attribute, value) {
    if (attribute && value) {
      this.filteredCoffee = this.availableCoffee.filter(
        (c) => c[attribute] === Number(value)
      );
    }
  }
  @action setAvailableCoffee(
    availableCoffee: AvailableCoffeeType[],
    selectedIcos?: AvailableCoffeeType[]
  ) {
    this.availableCoffee = availableCoffee;
    if (selectedIcos && selectedIcos.length > 0) {
      const icoIds = selectedIcos.map((ico) => ico.id);
      this.availableCoffee.forEach((ico) => {
        ico.checked = icoIds.includes(ico.id);
      });
    }
  }
}

export default OpportunityStore;
